import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { startCoinPayment } from "@/lib/coinpay";
import { toast } from "sonner";
import { WaiverGate } from "@/components/TournamentWaiver";
import { Handshake } from "lucide-react";

export default function PartnerInvites() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/partner-invites/mine"); setInvites(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || params.get("pinv") !== "1") return;
    params.delete("session_id"); params.delete("pinv"); setParams(params, { replace: true });
    let attempt = 0;
    const poll = async () => {
      if (attempt++ >= 6) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Payment received — your partner spot is confirmed! 🎾"); load(); return; }
        if (data.status === "expired") { toast.error("Payment session expired."); return; }
        setTimeout(poll, 2000);
      } catch { /* noop */ }
    };
    poll();
  }, []); // eslint-disable-line

  const [waived, setWaived] = useState({});
  const needsWaiver = (inv) => inv.kind === "tournament" && inv.tournament_id;
  const pay = async (inv) => {
    if (needsWaiver(inv) && !waived[inv.id]) { toast.error("Please read and check the Tournament Liability Waiver first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-invites/${inv.id}/pay`,
        { origin_url: window.location.origin + window.location.pathname });
      if (data.url) { window.location.href = data.url; return; }
      toast.success(data.perk_used || "You're confirmed — see you on the courts!");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (invites.length === 0) return null;
  return (
    <div className="space-y-3 mb-8" data-testid="partner-invites">
      {invites.map((inv) => (
        <div key={inv.id} data-testid={`partner-invite-${inv.id}`}
          className="border border-amber-500/50 bg-amber-500/5 p-4 flex flex-wrap items-center gap-4">
          <Handshake className="w-6 h-6 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold uppercase text-amber-300" data-testid={`invite-title-${inv.id}`}>
              {inv.inviter_name} picked you as their partner
            </div>
            <div className="text-sm text-zinc-300">
              {inv.event_name}{inv.event_type ? ` · ${inv.event_type}` : ""}{inv.team_name ? ` · Team ${inv.team_name}` : ""}{inv.event_date ? ` · ${inv.event_date}` : ""}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Your spot is reserved but shows as PENDING until your ${Number(inv.fee_usd || 0).toFixed(2)} entry is paid.</div>
            {needsWaiver(inv) && (
              <div className="mt-3"><WaiverGate tid={inv.tournament_id} eventName={inv.event_name} testid={`invite-waiver-${inv.id}`} onChange={(ok) => setWaived((w) => ({ ...w, [inv.id]: ok }))} /></div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button data-testid={`invite-pay-${inv.id}`} disabled={busy || (needsWaiver(inv) && !waived[inv.id])} onClick={() => pay(inv)}
              className="bg-amber-400 text-black font-black uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-amber-300 active:scale-95 transition-all disabled:opacity-50">
              {busy ? "…" : `Pay $${Number(inv.fee_usd || 0).toFixed(2)} & Confirm`}
            </button>
            {inv.fee_coins && (
              <button data-testid={`invite-coin-${inv.id}`} disabled={busy || (needsWaiver(inv) && !waived[inv.id])}
                onClick={() => { if (needsWaiver(inv) && !waived[inv.id]) { toast.error("Please read and check the Tournament Liability Waiver first"); return; } startCoinPayment(navigate, { kind: "partner_invite", item_id: inv.id }); }}
                className="border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest text-[10px] px-4 py-2 active:scale-95 transition-all disabled:opacity-50">
                🪙 Pay with Coin · {inv.fee_coins} BLC
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
